<template>
  <div class="page">
    <div class="container">
      <header class="header">
        <div class="brand">
          <svg
            t="1776082086858"
            class="back-icon"
            viewBox="0 0 1024 1024"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            p-id="3195"
            width="200"
            height="200"
            role="button"
            aria-label="返回列表"
            tabindex="0"
            @click="backToDashboard"
            @keydown.enter.prevent="backToDashboard"
            @keydown.space.prevent="backToDashboard"
          >
            <path
              d="M210.4 511.1 641.3 80.2c22.8-22.8 59.8-22.8 82.6 0 22.8 22.8 22.8 59.8 0 82.6L375.7 511.1l348.3 348.3c22.8 22.8 22.8 59.8 0 82.6-22.8 22.8-59.8 22.8-82.6 0L210.4 511.1 210.4 511.1zM210.4 511.1"
              p-id="3196"
            ></path>
          </svg>
          <img class="logo" src="/word_header_logo.png" alt="" aria-hidden="true" />
          <div>
            <div class="brand-title">AI视频面试平台</div>
            <div class="brand-sub">面试官入场 · 设备检测</div>
          </div>
        </div>
      </header>

      <main>
        <section class="content" aria-label="主内容区域">
          <div class="content-inner">
            <div class="step-content">
              <div class="grid">
                <div class="card">
                  <div class="card-h">
                    <strong>摄像头实时预览</strong>
                    <div class="btn-row">
                      <el-button size="small" native-type="button" class="ghost" @click="toggleFullscreen">全屏测试</el-button>
                    </div>
                  </div>
                  <div
                    ref="previewVideoWrap"
                    class="video-wrap"
                    :class="{ 'video-wrap--preview-flip-shell': previewMirrorOnShell }"
                  >
                    <div class="local-preview-flip">
                      <video
                        ref="preview"
                        class="local-mirror-preview"
                        :class="{ 'local-mirror-preview--css-flip': previewMirrorOnVideo }"
                        autoplay
                        playsinline
                        muted
                      ></video>
                    </div>
                    <button
                      v-if="isPreviewFullscreen"
                      type="button"
                      class="fs-exit-btn"
                      aria-label="退出全屏"
                      @click="exitFullscreen"
                    >
                      <img class="fs-exit-icon" :src="mobileIcons.fullscreenExit" alt="" aria-hidden="true" />
                    </button>
                    <div class="video-overlay">
                      <div class="badge-row" aria-label="实时提示">
                        <span class="badge" :class="camStatusType">
                          摄像头：{{ camStatusText }}
                        </span>
                        <span class="badge" :class="micStatusType">
                          麦克风：{{ micStatusText }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="card-b">
                    <div class="fineprint">
                      - 进入页面会自动请求摄像头/麦克风权限，并展示预览画面。<br />
                      - 请确保设备正常后再进入面试间。
                    </div>
                  </div>
                </div>

                <div class="card">
                  <div class="card-h">
                    <strong>设备检测状态</strong>
                    <div class="btn-row">
                      <el-button size="small" native-type="button" @click="recheck">重新检测</el-button>
                    </div>
                  </div>
                  <div class="card-b">
                    <div class="list">
                      <div class="item">
                        <div class="left">
                          <div class="icon" aria-hidden="true">📷</div>
                          <div class="meta">
                            <div class="k">摄像头检测</div>
                            <div class="d">{{ camDesc }}</div>
                          </div>
                        </div>
                        <div class="status" :class="camStatusType">
                          {{ camStatusText }}
                        </div>
                      </div>
                      <div class="item">
                        <div class="left">
                          <div class="icon" aria-hidden="true">🎙️</div>
                          <div class="meta">
                            <div class="k">麦克风检测</div>
                            <div class="d">{{ micDesc }}</div>
                          </div>
                        </div>
                        <div class="status" :class="micStatusType">
                          {{ micStatusText }}
                        </div>
                      </div>
                    </div>
                    <div class="hr"></div>
                    <div class="note">
                      小提示：如果摄像头没有画面，请检查浏览器地址栏的权限标记；建议使用 Chrome/Edge
                      最新版。
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="footer" style="display: flex; justify-content: center">
            <div class="btn-row">
              <el-button
                size="small"
                native-type="button"
                class="primary"
                :class="{ 'btn-muted': !deviceReady }"
                @click="enterRoom"
              >
                进入面试间
              </el-button>
            </div>
          </div>
        </section>
      </main>
    </div>
  </div>
</template>

<script>
import { isIosLikeDevice } from "@/utils/isIosLikeDevice";

export default {
  name: "InterviewerEntry",
  data() {
    return {
      stream: null,
      camOk: false,
      micOk: false,
      camDesc: "等待检测…",
      micDesc: "等待检测…",
      isChecking: false,
      isPreviewFullscreen: false,
      mobileIcons: {
        fullscreenExit: require("@/assets/icons/缩小@3x.png"),
      },
    };
  },
  mounted() {
    this.recheck();
    document.addEventListener("fullscreenchange", this.handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", this.handleFullscreenChange);
  },
  beforeDestroy() {
    document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
    document.removeEventListener("webkitfullscreenchange", this.handleFullscreenChange);
    this.stopStream();
  },
  computed: {
    /** iOS 上 transform 写在 video 上常不生效，改在包裹层翻转 */
    previewMirrorOnShell() {
      return isIosLikeDevice();
    },
    previewMirrorOnVideo() {
      return !isIosLikeDevice();
    },
    deviceReady() {
      return this.camOk && this.micOk;
    },
    camStatusType() {
      if (this.isChecking) return "warn";
      return this.camOk ? "ok" : "bad";
    },
    camStatusText() {
      if (this.isChecking) return "检测中";
      return this.camOk ? "正常" : "失败";
    },
    micStatusType() {
      if (this.isChecking) return "warn";
      return this.micOk ? "ok" : "bad";
    },
    micStatusText() {
      if (this.isChecking) return "检测中";
      return this.micOk ? "正常" : "失败";
    },
  },
  methods: {
    getFullscreenTarget() {
      return this.$refs.previewVideoWrap || null;
    },
    handleFullscreenChange() {
      const target = this.getFullscreenTarget();
      const activeEl = document.fullscreenElement || document.webkitFullscreenElement || null;
      this.isPreviewFullscreen = !!target && activeEl === target;
    },
    async recheck() {
      await this.startPreview();
    },
    async startPreview() {
      this.stopStream();
      this.camOk = false;
      this.micOk = false;
      this.isChecking = true;
      this.camDesc = "检测中…";
      this.micDesc = "检测中…";
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.camOk = false;
        this.micOk = false;
        this.camDesc = "当前浏览器不支持摄像头检测";
        this.micDesc = "当前浏览器不支持麦克风检测";
        this.isChecking = false;
        return;
      }
      let videoStream = null;
      let audioStream = null;
      try {
        videoStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      } catch (_) {
        this.camDesc = "摄像头检测失败，请检查权限或设备占用";
      }
      try {
        audioStream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
      } catch (_) {
        this.micDesc = "麦克风检测失败，请检查权限或设备占用";
      }

      const videoTracks = videoStream ? videoStream.getVideoTracks() : [];
      const audioTracks = audioStream ? audioStream.getAudioTracks() : [];
      this.camOk = videoTracks.length > 0;
      this.micOk = audioTracks.length > 0;

      // 调试：打印本次检测捕捉到的音频输入轨道详情
      if (audioTracks.length) {
        // eslint-disable-next-line no-console
        console.log(
          "[InterviewerEntry] captured audio tracks:",
          audioTracks.map((track) => ({
            id: track.id,
            kind: track.kind,
            label: track.label,
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
            settings: typeof track.getSettings === "function" ? track.getSettings() : {},
          }))
        );
      } else {
        // eslint-disable-next-line no-console
        console.log("[InterviewerEntry] captured audio tracks: []");
      }

      const combinedStream = new MediaStream();
      videoTracks.forEach((t) => combinedStream.addTrack(t));
      audioTracks.forEach((t) => combinedStream.addTrack(t));
      this.stream = combinedStream;

      if (this.camOk) {
        const v = this.$refs.preview;
        if (v) v.srcObject = combinedStream;
      }

      this.camDesc = this.camOk ? "已检测到可用摄像头并完成授权" : "未检测到摄像头";
      this.micDesc = this.micOk ? "已检测到麦克风并完成授权" : "未检测到麦克风";

      if (!this.camOk && !this.micOk) this.stopStream();
      this.isChecking = false;
    },
    stopStream() {
      if (!this.stream) return;
      try {
        this.stream.getTracks().forEach((t) => t.stop());
      } catch (_) {}
      this.stream = null;
    },
    async toggleFullscreen() {
      const el = this.getFullscreenTarget();
      if (!el) return;
      try {
        if (!document.fullscreenElement) await el.requestFullscreen();
        else await document.exitFullscreen();
      } catch (_) {
        this.$message.warning("全屏失败：浏览器可能阻止了全屏请求");
      }
    },
    async exitFullscreen() {
      try {
        if (document.fullscreenElement) await document.exitFullscreen();
      } catch (_) {
        this.$message.warning("退出全屏失败，请按 Esc 键重试");
      }
    },
    enterRoom() {
      if (!this.deviceReady) {
        this.$message.warning("请先完成设备检测");
        return;
      }
      const q = { ...this.$route.query };
      this.$router.push({ path: "/interviewer/room", query: q });
    },
    backToDashboard() {
      this.$router.push("/interviewer/dashboard");
    },
  },
};
</script>

<style scoped>
.page {
  min-height: 100%;
  padding: 18px 16px 26px;
  background: #f5f7fa;
}

.container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 28px 18px 42px 18px;
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(10px);
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 750;
  letter-spacing: 0.2px;
}

.logo {
  width: 90px;
  height: 32px;
  display: block;
  object-fit: contain;
  background: transparent;
}

.back-icon {
  width: 18px;
  height: 18px;
  min-width: 18px;
  fill: #334155;
  cursor: pointer;
}

.back-icon:hover {
  fill: #2563eb;
}

.back-icon:focus {
  outline: none;
}

.back-icon:focus-visible {
  outline: 2px solid rgba(37, 99, 235, 0.35);
  outline-offset: 2px;
  border-radius: 4px;
}

.brand-title {
  font-size: 14px;
  opacity: 0.92;
}

.brand-sub {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

main {
  margin-top: 16px;
}

.content {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.content-inner {
  padding: 18px;
}

.grid {
  display: grid;
  grid-template-columns: 1.25fr 0.95fr;
  gap: 16px;
}

.card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  background: #f8fafc;
  overflow: hidden;
}

.card-h {
  padding: 12px 14px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
}

.card-h strong {
  font-weight: 800;
}

.card-b {
  padding: 14px;
}

.video-wrap {
  position: relative;
  aspect-ratio: 16 / 9;
  background: #000;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.fs-exit-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 999px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.7);
  color: #ffffff;
  cursor: pointer;
}

.fs-exit-icon {
  width: 18px;
  height: 18px;
  display: block;
}

.fs-exit-btn:hover {
  background: rgba(15, 23, 42, 0.9);
}

video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.local-preview-flip {
  width: 100%;
  height: 100%;
}

/* iOS：在包裹层水平翻转（video 上 transform 在部分 WebKit 上无效） */
.video-wrap--preview-flip-shell .local-preview-flip {
  -webkit-transform: scaleX(-1);
  transform: scaleX(-1);
  transform-origin: center center;
}

/* 非 iOS：与面试间本地预览一致，仅在 video 上翻转 */
video.local-mirror-preview.local-mirror-preview--css-flip {
  -webkit-transform: scaleX(-1);
  transform: scaleX(-1);
  transform-origin: center center;
}

.video-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 12px;
  background: linear-gradient(180deg, transparent 50%, rgba(0, 0, 0, 0.55));
  pointer-events: none;
}

.badge-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.badge {
  pointer-events: none;
  font-size: 10px;
  padding: 4px 8px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  color: #fff;
}

.badge.ok {
  background: rgba(34, 197, 94, 0.8);
}

.badge.warn {
  background: rgba(245, 158, 11, 0.8);
}

.badge.bad {
  background: rgba(239, 68, 68, 0.8);
}

.btn-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

button {
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #ffffff;
  color: #1a1a2e;
  padding: 10px 12px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 700;
  font-size: 13px;
}

button:hover {
  background: #f8fafc;
}

button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.primary {
  border-color: rgba(37, 99, 235, 0.3);
  background: #2563eb;
  color: #fff;
}

.primary:hover {
  background: #1d4ed8;
}

.primary.btn-muted {
  opacity: 0.55;
  cursor: not-allowed;
  background: #94a3b8;
  border-color: rgba(148, 163, 184, 0.6);
  color: #fff;
}

.primary.btn-muted:hover {
  background: #94a3b8;
}

.ghost {
  background: transparent;
}

.list {
  display: grid;
  gap: 10px;
}

.item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 10px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  background: #ffffff;
}

.left {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.icon {
  width: 24px;
  height: 24px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #f1f5f9;
  flex: 0 0 auto;
  margin-top: 1px;
}

.meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.meta .k {
  font-weight: 800;
}

.meta .d {
  color: #6b7280;
  font-size: 12px;
}

.status {
  flex: 0 0 auto;
  font-weight: 800;
  font-size: 12px;
  padding: 6px 9px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #f1f5f9;
  margin-top: 1px;
}

.status.ok {
  border-color: rgba(34, 197, 94, 0.3);
  background: rgba(34, 197, 94, 0.1);
  color: #166534;
}

.status.warn {
  border-color: rgba(245, 158, 11, 0.3);
  background: rgba(245, 158, 11, 0.1);
  color: #b45309;
}

.status.bad {
  border-color: rgba(239, 68, 68, 0.3);
  background: rgba(239, 68, 68, 0.1);
  color: #b91c1c;
}

.fineprint {
  margin-top: 8px;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.55;
}

.hr {
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 12px 0;
}

.note {
  color: #6b7280;
  font-size: 12px;
  max-width: 720px;
  line-height: 1.5;
}

.footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 18px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #f8fafc;
}

@media (max-width: 768px) {
  .container {
    padding: 0;
  }

  .grid {
    grid-template-columns: 1fr;
  }
}
</style>
